import { onCleanup, onMount } from "solid-js";
import * as THREE from "three";
import { buildPlayerGroup, loadSkinImage } from "../renderer/skinModel";

interface SkinViewer3DProps {
  skinUrl: string;
  slim: boolean;
  size?: number;
}

export default function SkinViewer3D(props: SkinViewer3DProps) {
  let canvas: HTMLCanvasElement | undefined;

  onMount(() => {
    if (!canvas) return;
    const size = props.size ?? 280;
    const renderer = new THREE.WebGLRenderer({ canvas, alpha: true, antialias: true });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.setSize(size, size, false);
    renderer.setClearColor(0x000000, 0);

    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(40, 1, 0.1, 1000);
    camera.position.set(0, 0, 62);
    camera.lookAt(0, 0, 0);

    let group: THREE.Group | undefined;
    let yaw = THREE.MathUtils.degToRad(18);
    let autoRotate = true;
    let dragging = false;
    let lastX = 0;
    let raf = 0;

    loadSkinImage(props.skinUrl)
      .then((img) => {
        group = buildPlayerGroup(img, props.slim);
        group.position.y = 2;
        scene.add(group);
      })
      .catch(() => {});

    const onPointerDown = (e: PointerEvent) => {
      dragging = true;
      autoRotate = false;
      lastX = e.clientX;
      canvas!.setPointerCapture(e.pointerId);
    };
    const onPointerMove = (e: PointerEvent) => {
      if (!dragging) return;
      yaw += (e.clientX - lastX) * 0.01;
      lastX = e.clientX;
    };
    const onPointerUp = () => {
      dragging = false;
    };

    canvas.addEventListener("pointerdown", onPointerDown);
    canvas.addEventListener("pointermove", onPointerMove);
    canvas.addEventListener("pointerup", onPointerUp);
    canvas.addEventListener("pointerleave", onPointerUp);

    const animate = () => {
      raf = requestAnimationFrame(animate);
      if (autoRotate) yaw += 0.012;
      if (group) group.rotation.y = yaw;
      renderer.render(scene, camera);
    };
    animate();

    onCleanup(() => {
      cancelAnimationFrame(raf);
      canvas!.removeEventListener("pointerdown", onPointerDown);
      canvas!.removeEventListener("pointermove", onPointerMove);
      canvas!.removeEventListener("pointerup", onPointerUp);
      canvas!.removeEventListener("pointerleave", onPointerUp);
      if (group) {
        group.traverse((obj) => {
          const mesh = obj as THREE.Mesh;
          if (!mesh.isMesh) return;
          mesh.geometry.dispose();
          const mats = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
          for (const mat of mats) {
            (mat as THREE.MeshBasicMaterial).map?.dispose();
            mat.dispose();
          }
        });
      }
      renderer.dispose();
    });
  });

  return (
    <canvas
      ref={canvas}
      style={{
        width: `${props.size ?? 280}px`,
        height: `${props.size ?? 280}px`,
        cursor: "grab",
        "touch-action": "none",
      }}
    />
  );
}
